import React, { useState, useEffect, useRef } from 'react';
import { PART3_QUESTIONS, PART3_FOLLOWUP_QUESTIONS, PART3_SPECIFIC_FOLLOWUPS, LISTENING_TIME, speakText, getAIFollowUpQuestion } from './TrainingContainer';
import { useLanguage } from '../contexts/LanguageContext';
import { isIPadSafari } from '../utils/deviceDetection';
import TipButton from './TipButton';

const Part3Training = ({ 
  username, 
  setShowCompletionModal, 
  setCurrentMessages,
  onRepeatTopic,
  onNewRound,
  onEndTraining
}) => {
  // 添加语言上下文
  const { t, language } = useLanguage();
  
  // 检测iPad设备
  const [isIPad, setIsIPad] = useState(false);
  
  useEffect(() => {
    const checkIPad = () => {
      const isIPadDevice = /iPad/.test(navigator.userAgent) || 
                          (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
      setIsIPad(isIPadDevice);
    };
    checkIPad();
  }, []);
  
  // 练习相关状态
  const [prepTime, setPrepTime] = useState(0); // Part 3没有准备时间
  const [practiceTime, setPracticeTime] = useState(60); // Part 3回答时间调整为60秒
  const [timer, setTimer] = useState(practiceTime);
  const [gamePhase, setGamePhase] = useState('idle');
  
  // 录音相关状态
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const fullTranscriptionRef = useRef('');

  // Part 3相关状态
  const [part3Question1Time, setPart3Question1Time] = useState(60);
  const [part3Question2Time, setPart3Question2Time] = useState(60);
  const [part3Question3Time, setPart3Question3Time] = useState(60);
  const [part3Question4Time, setPart3Question4Time] = useState(60);

  // 添加当前问题编号状态
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState(1);
  
  // 添加听题阶段标志
  const [isListeningPhase, setIsListeningPhase] = useState(false);

  // 添加对话消息状态
  const [part3Messages, setPart3Messages] = useState([
    {
      type: 'mentor',
      content: t('welcome_to_part3'),
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  
  // 创建聊天消息容器的引用
  const messagesEndRef = useRef(null);

  // 自动滚动到最新消息
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // 优化滚动触发时机，确保在DOM更新后滚动
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(scrollToBottom);
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [part3Messages]);

  // 处理倒计时
  useEffect(() => {
    if (gamePhase === 'idle' || gamePhase === 'completed') {
      return;
    }

    const countdown = setInterval(() => {
      setTimer(prevTimer => {
        if (prevTimer <= 1) {
          clearInterval(countdown);
          
          if (gamePhase === 'listening') {
            console.log('听题阶段结束，开始回答阶段 - 当前问题编号:', currentQuestionNumber);
            setIsListeningPhase(false);
            setGamePhase('practice');
            
            // 设置当前问题的回答时间
            let answerTime;
            switch(currentQuestionNumber) {
              case 1:
                answerTime = part3Question1Time;
                break;
              case 2:
                answerTime = part3Question2Time;
                break;
              case 3:
                answerTime = part3Question3Time;
                break;
              case 4:
                answerTime = part3Question4Time;
                break;
              default:
                answerTime = 60;
            }
            
            setTimer(answerTime);
            console.log(`设置问题${currentQuestionNumber}的回答时间为${answerTime}秒`);
            
            // 开始录音
            startSpeechRecognition();
          } else if (gamePhase === 'practice') {
            console.log('练习阶段结束 - 当前问题编号:', currentQuestionNumber);
            // 结束录音并发送转录内容
            if (isRecording) {
              console.log('准备停止语音识别');
              // 添加一个小延迟再停止录音，确保最后几秒内容能完整捕获
              setTimeout(() => {
                stopSpeechRecognition();
              }, 1500); // 增加1.5秒缓冲时间
            }
            return 0; // 即使给了额外缓冲时间，UI上的计时器仍然显示为0
          }
          
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);

    // 清理函数
    return () => {
      clearInterval(countdown);
      // 如果组件卸载时正在录音，确保停止录音
      if (isRecording) {
        console.log('组件卸载，停止录音');
        stopSpeechRecognition();
      }
    };
  }, [gamePhase, practiceTime, isRecording, currentQuestionNumber, part3Question1Time, part3Question2Time, part3Question3Time, part3Question4Time]);

  // 添加对gamePhase变化的监听
  useEffect(() => {
    console.log("游戏阶段变化为:", gamePhase, "当前问题编号:", currentQuestionNumber, "是否为听题阶段:", isListeningPhase);
  }, [gamePhase, currentQuestionNumber, isListeningPhase]);

  // 添加启动语音识别函数
  const startSpeechRecognition = () => {
    console.log('开始初始化语音识别...');
    
    // 检查浏览器支持
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      console.error('浏览器不支持语音识别');
      alert('您的浏览器不支持语音识别功能，请使用Chrome浏览器');
      return;
    }
    
      try {
      // 创建语音识别实例
      console.log('创建语音识别实例...');
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      // 配置语音识别
          recognitionRef.current.continuous = true;
          recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      
      // 清空先前的转录
      fullTranscriptionRef.current = '';
      setTranscription('');
          
      // 处理结果
          recognitionRef.current.onresult = (event) => {
            let interimTranscript = '';
            let finalTranscript = '';
            
            for (let i = event.resultIndex; i < event.results.length; i++) {
              const transcript = event.results[i][0].transcript;
              if (event.results[i].isFinal) {
                finalTranscript += transcript + ' ';
            console.log('识别到最终转录片段:', transcript);
              } else {
                interimTranscript += transcript;
            console.log('识别到临时转录:', transcript);
              }
            }
            
        // 更新全局转录和显示转录
        if (finalTranscript) {
          fullTranscriptionRef.current += finalTranscript;
          console.log('更新全局转录:', fullTranscriptionRef.current);
        }
        
        setTranscription(fullTranscriptionRef.current + interimTranscript);
          };
      
      // 处理错误
      recognitionRef.current.onerror = (event) => {
        console.error('语音识别错误:', event.error);
        
        // 如果是iPad Safari上的错误，不显示错误提示，因为我们使用了替代方案
        if (!isIPadSafari()) {
          if (event.error === 'no-speech') {
            console.log('未检测到语音');
          } else {
            alert(`语音识别错误: ${event.error}`);
          }
        }
        
        // 对于"no-speech"错误，我们不停止录音，让用户继续尝试
        if (event.error !== 'no-speech') {
          setIsRecording(false);
        }
      };
      
      // 处理结束
      recognitionRef.current.onend = () => {
        console.log('语音识别结束');
        
        // 在iPad Safari上，我们使用的是替代方案，不需要在这里重新启动
        if (!isIPadSafari() && isRecording) {
          // 如果仍在录音状态，自动重新开始录音
          try {
            console.log('重新启动语音识别');
            recognitionRef.current.start();
          } catch (e) {
            console.error('重新启动语音识别失败:', e);
          }
        }
      };
      
      // 开始识别
      recognitionRef.current.start();
      setIsRecording(true);
      console.log('语音识别已启动');
      
    } catch (e) {
      console.error('启动语音识别失败:', e);
      alert('启动语音识别失败，请刷新页面重试');
    }
  };

  const stopSpeechRecognition = async () => {
    if (recognitionRef.current) {
      console.log('停止录音');
      
      // 防止重复调用
      if (!isRecordingRef.current) {
        console.log('录音已经停止，跳过处理');
        return;
      }
      
      isRecordingRef.current = false;
      setIsRecording(false);
      
      // 存储当前转录内容
      const currentTranscription = fullTranscriptionRef.current.trim();
      console.log('当前转录内容:', currentTranscription);
      
      // 重置引用中的内容，为下一次录音做准备
      fullTranscriptionRef.current = '';
      
      try {
        recognitionRef.current.stop();
        console.log('录音已停止');

        // 添加小延迟确保所有语音处理完成
        setTimeout(() => {
          // 始终添加用户消息，即使转录内容为空
          const content = currentTranscription || t('no_speech_detected_part3');
          console.log('发送转录内容到对话框:', content);
          const userMessage = {
            type: 'user',
            content: content,
            time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
          };
          
          setPart3Messages(prev => {
            const newMessages = [...prev, userMessage];
            
            // If this is the last question (4th), show the completion modal
            if (currentQuestionNumber >= 4) {
              // Send messages to container for potential download
              setCurrentMessages(newMessages);
              // Show completion modal
              setTimeout(() => setShowCompletionModal(true), 500);
            }
            
            return newMessages;
          });
          
          if (currentQuestionNumber < 4) {
            // If not the last question, generate follow-up and continue
            console.log('准备进入下一个问题, 当前问题编号:', currentQuestionNumber);
            setCurrentQuestionNumber(prevNum => {
              const nextNum = prevNum + 1;
              console.log('更新问题编号 从', prevNum, '到', nextNum);
              return nextNum;
            });
            
            // 使用setTimeout确保状态更新后再调用
            setTimeout(() => {
              console.log('生成追问前的问题编号:', currentQuestionNumber + 1);
              generatePart3FollowupQuestion();
            }, 100);
          }
        }, 500); // 给语音识别引擎0.5秒的时间完成处理
        
      } catch (error) {
        console.error('停止语音识别时出错:', error);
      }
    }
  };

  // 添加生成Part 3追问问题的函数
  const generatePart3FollowupQuestion = async () => {
    // 获取最后一条用户消息（考生回答）和最后一条考官问题
    const userMessages = part3Messages.filter(m => m.type === 'user');
    const lastUserMessage = userMessages.length > 0 ? userMessages[userMessages.length - 1] : null;
    
    const mentorMessages = part3Messages.filter(m => m.type === 'mentor' && m.isVoice);
    const lastMentorMessage = mentorMessages.length > 0 ? mentorMessages[mentorMessages.length - 1] : null;
    
    // 添加考官思考状态消息
    const thinkingMessage = {
      type: 'mentor',
      content: t('examiner_thinking'),
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    };
    setPart3Messages(prev => [...prev, thinkingMessage]);
    
    // 设置延迟，模拟考官思考
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    let followupQuestion;
    
    // 通过API或从预设问题中获取追问问题
    if (lastUserMessage && lastMentorMessage) {
      // 检查是否第二个问题以上，以及是否有特定的追问问题
      if (currentQuestionNumber >= 2 && lastMentorMessage.content in PART3_SPECIFIC_FOLLOWUPS) {
        // 使用特定追问问题
        const specificFollowups = PART3_SPECIFIC_FOLLOWUPS[lastMentorMessage.content];
        const questionIndex = currentQuestionNumber - 2; // 追问从0开始
        
        // 如果有对应的特定追问
        if (questionIndex < specificFollowups.length) {
          followupQuestion = specificFollowups[questionIndex];
        }
      }
      
      // 如果没有找到特定追问，尝试API生成
      if (!followupQuestion) {
        try {
          followupQuestion = await getAIFollowUpQuestion(
            lastMentorMessage.content, 
            lastUserMessage.content
          );
        } catch (error) {
          console.error('API调用失败:', error);
        }
      }
    }
    
    // 如果API调用失败或没有上一条消息，使用预设追问
    if (!followupQuestion) {
      const randomIndex = Math.floor(Math.random() * PART3_FOLLOWUP_QUESTIONS.length);
      followupQuestion = PART3_FOLLOWUP_QUESTIONS[randomIndex];
    }
    
    // 移除思考消息
    setPart3Messages(prev => 
      prev.filter(m => m.content !== t('examiner_thinking'))
    );
    
    // 添加追问问题
    const followupMessage = {
      type: 'mentor',
      isVoice: true,
      content: followupQuestion,
      displayTextHidden: true,
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    };
    
    setPart3Messages(prev => [...prev, followupMessage]);
    
    // 播放语音问题
    speakText(followupQuestion);
    
    // 设置阶段和计时器
    setGamePhase('listening');
    setIsListeningPhase(true);
    setTimer(LISTENING_TIME);
  };

  // 调整Part 3问题时间的函数
  const adjustPart3QuestionTime = (questionNumber, amount) => {
    switch(questionNumber) {
      case 1:
        setPart3Question1Time(Math.max(5, part3Question1Time + amount));
        break;
      case 2:
        setPart3Question2Time(Math.max(5, part3Question2Time + amount));
        break;
      case 3:
        setPart3Question3Time(Math.max(5, part3Question3Time + amount));
        break;
      case 4:
        setPart3Question4Time(Math.max(5, part3Question4Time + amount));
        break;
      default:
        break;
    }
  };

  // 开始Part 3练习
  const startPart3Practice = () => {
    if (gamePhase !== 'idle') return;
    
    console.log('Starting Part 3 practice');
    
    // Reset question number
    setCurrentQuestionNumber(1);
    
    // Initialize a random question
    let initialQuestion;
    
    // Check if there's a cached question
    const cachedQuestion = localStorage.getItem('part3CurrentQuestion');
    
    if (cachedQuestion) {
      // Use cached question if available
      initialQuestion = cachedQuestion;
      localStorage.removeItem('part3CurrentQuestion'); // Clear the cache
    } else {
      // 随机选择一个Part 3问题作为初始问题
      const randomIndex = Math.floor(Math.random() * PART3_QUESTIONS.length);
      initialQuestion = PART3_QUESTIONS[randomIndex];
    }
    
    // 清空之前的消息，重新开始
    setPart3Messages([{
      type: 'mentor',
      content: t('part3_start_training'),
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    }, {
      type: 'mentor',
      isVoice: true,
      content: initialQuestion,
      displayTextHidden: true, // 添加标记表示不显示文字
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    }]);
    
    // 播放语音问题
    speakText(initialQuestion);
    
    // 进入听题阶段
    setGamePhase('listening');
    setIsListeningPhase(true);
    setTimer(LISTENING_TIME);
  };

  // 添加处理训练完成后的重置函数
  useEffect(() => {
    // 监听完成弹窗关闭事件
    const handleCompletionModalClosed = (event) => {
      if (event.detail && event.detail.part === 'part3') {
        console.log("检测到Part3训练完成弹窗关闭事件，重置训练状态");
        // 重置状态到初始状态
        handleResetTraining();
      }
    };

    // 添加事件监听
    window.addEventListener('completionModalClosed', handleCompletionModalClosed);

    // 清理函数
    return () => {
      window.removeEventListener('completionModalClosed', handleCompletionModalClosed);
    };
  }, []);

  // 重置训练状态的函数
  const handleResetTraining = () => {
    console.log("重置训练状态到初始状态");
    // 重置游戏阶段
    setGamePhase('idle');
    // 重置听题阶段标志
    setIsListeningPhase(false);
    // 重置问题编号
    setCurrentQuestionNumber(1);
    // 重置消息
    setPart3Messages([{
      type: 'mentor',
      content: t('welcome_to_part3'),
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    }]);
    // 重置录音状态
    if (isRecordingRef.current) {
      try {
        recognitionRef.current?.stop();
      } catch (e) {
        console.error("停止语音识别失败:", e);
      }
      isRecordingRef.current = false;
      setIsRecording(false);
    }
    // 重置转录
    setTranscription('');
    // 重置计时器
    setTimer(practiceTime);
  };

  // Add handlers for modal actions
  const handleRepeatTopic = () => {
    // Reset listening phase state
    setIsListeningPhase(false);
    setCurrentQuestionNumber(1);
    
    // Get the last question used
    const lastQuestion = part3Messages.find(m => m.isVoice && m.content)?.content;
    
    // Save for reuse if found
    if (lastQuestion) {
      localStorage.setItem('part3CurrentQuestion', lastQuestion);
    }
    
    // Reset messages
    setPart3Messages([{
      type: 'mentor',
      content: t('welcome_to_part3'),
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    }]);
    
    setTranscription('');
    setGamePhase('idle');
    setTimer(practiceTime);
    
    // Call parent handler if provided
    if (onRepeatTopic) onRepeatTopic();
  };
  
  const handleNewRound = () => {
    // Reset listening phase state
    setIsListeningPhase(false);
    setCurrentQuestionNumber(1);
    
    // Reset messages
    setPart3Messages([{
      type: 'mentor',
      content: t('welcome_to_part3'),
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    }]);
    
    setTranscription('');
    setGamePhase('idle');
    setTimer(practiceTime);
    
    // Remove any saved last question
    localStorage.removeItem('part3CurrentQuestion');
    
    // Call parent handler if provided
    if (onNewRound) onNewRound();
  };

  // Add resetTrainingState useEffect after all other function definitions
  useEffect(() => {
    const resetTrainingState = (event) => {
      // 确保只有当前组件接收到针对它的重置事件
      if (event.detail.part === 'part3') {
        // 重置听题阶段状态
        setIsListeningPhase(false);
        setCurrentQuestionNumber(1);
        
        // 重置消息
        setPart3Messages([{
          type: 'mentor',
          content: t('welcome_to_part3'),
          time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
        }]);
        
        // 重置录音和转录状态
        setTranscription('');
        if (isRecordingRef.current) {
          stopSpeechRecognition();
        }
        setGamePhase('idle');
        setTimer(practiceTime);
        
        // 移除任何保存的上一题
        localStorage.removeItem('part3CurrentQuestion');
      }
    };
    
    // 添加事件监听器
    window.addEventListener('resetTrainingState', resetTrainingState);
    
    // 清理函数
    return () => {
      window.removeEventListener('resetTrainingState', resetTrainingState);
    };
  }, [practiceTime]); // Simplified dependency array

  // Add a new function to change the topic during listening phase
  const handleChangeTopic = () => {
    // Log the current situation
    console.log("当前问题编号:", currentQuestionNumber, "是否为听题阶段:", isListeningPhase);
    
    // Only allow topic change during the listening phase of first question
    if (isListeningPhase && currentQuestionNumber === 1) {
      // Randomly select a new question
      const randomIndex = Math.floor(Math.random() * PART3_QUESTIONS.length);
      const newQuestion = PART3_QUESTIONS[randomIndex];
      
      // Update the most recent mentor voice message with this new question
      setPart3Messages(prevMessages => {
        // Create a copy of the messages
        const newMessages = [...prevMessages];
        
        // Find the last mentor voice message (should be the second one)
        const lastMentorVoiceMessageIndex = newMessages.findIndex(
          msg => msg.type === 'mentor' && msg.isVoice
        );
        
        if (lastMentorVoiceMessageIndex !== -1) {
          // Update its content with the new question
          newMessages[lastMentorVoiceMessageIndex] = {
            ...newMessages[lastMentorVoiceMessageIndex],
            content: newQuestion,
          };
        }
        
        return newMessages;
      });
      
      // Play the new question
      speakText(newQuestion);
      
      // Reset the timer for the listening phase
      setTimer(LISTENING_TIME);
    }
  };

  // Add console.log when listening phase changes or question number changes
  useEffect(() => {
    console.log("Listening phase changed to:", isListeningPhase, "Current question:", currentQuestionNumber);
  }, [isListeningPhase, currentQuestionNumber]);

  // Monitor question changes
  useEffect(() => {
    console.log("Question number changed to:", currentQuestionNumber);
  }, [currentQuestionNumber]);

  // 添加播放语音消息的函数
  const playVoiceMessage = (message) => {
    if (message.isVoice && message.content) {
      console.log('尝试播放语音消息:', message.content);
      
      // 检查浏览器支持
      if (!window.speechSynthesis) {
        console.error('浏览器不支持语音合成');
        alert('您的浏览器不支持语音合成功能，请使用Chrome浏览器');
        return;
      }
      
      // 使用Web Speech API播放语音
      try {
        const utterance = new SpeechSynthesisUtterance(message.content);
        utterance.lang = 'en-US'; // 英语发音
        utterance.rate = 0.9; // 语速稍慢一些，更清晰
        
        window.speechSynthesis.cancel(); // 取消当前正在播放的语音
        window.speechSynthesis.speak(utterance);
        
        console.log('语音播放已启动');
      } catch (error) {
        console.error('语音合成错误:', error);
        alert('语音播放失败，请刷新页面重试');
      }
    }
  };

  return (
    <>
      <TipButton content={t('part3_preparation_note')} language={language} />
      {/* iPad语音提示 */}
      {isIPad && (
        <div style={{
          position: 'absolute',
          top: '400px',
          left: '20px',
          width: '220px',
          backgroundColor: '#e3f2fd',
          padding: '1rem',
          borderRadius: '8px',
          border: '2px solid #2196f3',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 10,
          fontSize: '0.85rem',
          lineHeight: '1.5'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#1976d2' }}>
            {language === 'en' ? '🔊 iPad User Tip' : '🔊 iPad用户提示'}
          </div>
          <div style={{ color: '#333' }}>
            {language === 'en' 
              ? 'If the audio doesn\'t play automatically, please tap the "Listen Again" button to hear the question.'
              : '如果语音没有自动播放，请点击"重听"按钮收听问题。'}
          </div>
        </div>
      )}
      <div className="part-content">
        <div className="part3-content-area">
          <div className="part2-header">
            <h2>{t('part3_title')}</h2>
            <p>{t('part3_description')}</p>
          </div>
          
          <div className="part3-main-container">
            {/* 对话框移到主要位置 */}
            <div className="part3-chat-panel">
              {/* 完全重写聊天头部 */}
              <div className="chat-header" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '10px 15px',
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid #e0e0e0'
              }}>
                <div className="chat-title" style={{ fontWeight: 'bold' }}>{t('question_answer_chat')}</div>
                {/* 确保按钮只在第一个问题的听题阶段显示为可用 */}
                <button 
                  onClick={handleChangeTopic}
                  disabled={!isListeningPhase || currentQuestionNumber !== 1}
                  style={{ 
                    backgroundColor: (isListeningPhase && currentQuestionNumber === 1) ? '#4a90e2' : '#cccccc', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    padding: '6px 12px',
                    cursor: (isListeningPhase && currentQuestionNumber === 1) ? 'pointer' : 'not-allowed',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {t('change_topic')} {currentQuestionNumber === 1 ? '✓' : '✗'}
                </button>
              </div>
              <div className="chat-messages">
                {part3Messages.map((message, index) => (
                  <div key={index} className={`message ${message.type}`}>
                    <div className="avatar-container">
                      <div className="user-name">{message.type === 'mentor' ? t('examiner') : (username || t('candidate'))}</div>
                      <div className={`avatar ${message.type}`}>
                        {message.type === 'mentor' ? t('examiner')[0] : (username ? username[0] : t('candidate')[0])}
                      </div>
                    </div>
                    <div className="message-wrapper">
                      <div className="message-content">
                        {message.isVoice ? (
                          // 语音消息的特殊渲染，添加重听按钮
                          <div className="voice-message-container">
                            <div className="voice-message">
                              <span className="voice-icon">🔊</span>
                              <div className="voice-waveform">
                                <div className="wave"></div>
                                <div className="wave"></div>
                                <div className="wave"></div>
                                <div className="wave"></div>
                              </div>
                              {!message.displayTextHidden && (
                                <span className="voice-text">{message.content}</span>
                              )}
                            </div>
                            {/* 只在听题阶段或游戏未开始时显示重听按钮 */}
                            {(isListeningPhase || gamePhase === 'idle') && (
                              <button 
                                onClick={() => speakText(message.content)}
                                className="relisten-button"
                              >
                                {t('listen_again')}
                              </button>
                            )}
                          </div>
                        ) : (
                          message.content
                        )}
                      </div>
                      <div className="message-time">{message.time}</div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              
              {/* 录音状态指示器和转录显示 - 仅在practice阶段显示 */}
              {gamePhase === 'practice' && (
                <div className="part3-recording-container">
                  <div className="part3-recording-status">
                    <div className="recording-indicator">
                      <div className="recording-wave"></div>
                      <div className="recording-wave"></div>
                      <div className="recording-wave"></div>
                      <div className="recording-wave"></div>
                      <div className="recording-wave"></div>
                    </div>
                    <span className="recording-text">{t('recording_status')}</span>
                  </div>
                  {isRecording && (
                    <div className="transcription-container">
                      <div className="transcription-content">
                        {transcription}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* 时间设置面板 */}
            <div className="part3-timer">
              <div className="timer-section">
                <div className="timer-label">{t('question1_answer_time')}</div>
                <div className="timer-controls">
                  <button className="timer-btn" onClick={() => adjustPart3QuestionTime(1, -5)}>-</button>
                  <span className="timer-value">{part3Question1Time}{t('seconds')}</span>
                  <button className="timer-btn" onClick={() => adjustPart3QuestionTime(1, 5)}>+</button>
                </div>
              </div>
              
              <div className="timer-section">
                <div className="timer-label">{t('question2_answer_time')}</div>
                <div className="timer-controls">
                  <button className="timer-btn" onClick={() => adjustPart3QuestionTime(2, -5)}>-</button>
                  <span className="timer-value">{part3Question2Time}{t('seconds')}</span>
                  <button className="timer-btn" onClick={() => adjustPart3QuestionTime(2, 5)}>+</button>
                </div>
              </div>
              
              <div className="timer-section">
                <div className="timer-label">{t('question3_answer_time')}</div>
                <div className="timer-controls">
                  <button className="timer-btn" onClick={() => adjustPart3QuestionTime(3, -5)}>-</button>
                  <span className="timer-value">{part3Question3Time}{t('seconds')}</span>
                  <button className="timer-btn" onClick={() => adjustPart3QuestionTime(3, 5)}>+</button>
                </div>
              </div>
              
              <div className="timer-section">
                <div className="timer-label">{t('question4_answer_time')}</div>
                <div className="timer-controls">
                  <button className="timer-btn" onClick={() => adjustPart3QuestionTime(4, -5)}>-</button>
                  <span className="timer-value">{part3Question4Time}{t('seconds')}</span>
                  <button className="timer-btn" onClick={() => adjustPart3QuestionTime(4, 5)}>+</button>
                </div>
              </div>
              
              {/* 修改Part 3倒计时显示，添加听题阶段 */}
              {gamePhase !== 'idle' && (
                <div className="part3-countdown">
                  <div className="phase-indicator">
                    {isListeningPhase 
                      ? t('question_listening_phase').replace('{number}', currentQuestionNumber) 
                      : t('question_answering_phase').replace('{number}', currentQuestionNumber)}
                  </div>
                  <div className="countdown-display">
                    <span className="countdown-number">{timer}</span>
                    <span className="countdown-unit">{t('seconds')}</span>
                  </div>
                  {isListeningPhase && (
                    <div className="listening-tip">
                      <span className="tip-icon">💡</span>
                      <span className="tip-text">
                        {currentQuestionNumber === 1 
                          ? t('listening_tip_first') 
                          : t('listening_tip_followup')}
                      </span>
                    </div>
                  )}
                </div>
              )}
              
              <button 
                className="start-practice-btn" 
                onClick={startPart3Practice}
                disabled={gamePhase !== 'idle'}
              >
                {gamePhase === 'idle' ? t('start_practice') : t('practicing')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Part3Training; 